import { useMutation } from "@tanstack/vue-query";
import { onBeforeUnmount } from "vue";
import MessageDraftService from "@/services/message-draft";
import type { MessageDraftChannel } from "@/dtos/MessageDraft.dto";

const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * Persists a per-conversation message draft to the backend so it survives
 * page reloads / device switches. Autosave is debounced; save/clear are
 * flushed immediately when switching conversations or sending a message.
 */
export function useMessageDraft(channel: MessageDraftChannel) {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const saveMutation = useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      MessageDraftService.saveDraft(conversationId, channel, content),
  });

  const clearMutation = useMutation({
    mutationFn: (conversationId: string) => MessageDraftService.clearDraft(conversationId, channel),
  });

  const cancelPendingSave = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const fetchDraft = async (conversationId: string): Promise<string> => {
    if (!conversationId) return "";
    try {
      const response = await MessageDraftService.getDraft(conversationId, channel);
      return response.data?.data?.content || "";
    } catch {
      return "";
    }
  };

  const scheduleSave = (conversationId: string, content: string) => {
    if (!conversationId) return;
    cancelPendingSave();
    debounceTimer = setTimeout(() => {
      saveMutation.mutate({ conversationId, content });
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const flushSave = (conversationId: string, content: string) => {
    cancelPendingSave();
    if (!conversationId) return;
    saveMutation.mutate({ conversationId, content });
  };

  const clearDraft = (conversationId: string) => {
    cancelPendingSave();
    if (!conversationId) return;
    clearMutation.mutate(conversationId);
  };

  onBeforeUnmount(cancelPendingSave);

  return {
    fetchDraft,
    scheduleSave,
    flushSave,
    clearDraft,
  };
}
